import React, { useState } from 'react';
import { FaSearchPlus, FaSearchMinus, FaUndo } from 'react-icons/fa';
import OcorrenciaTemplate from '~/Components/Negocios/CaixaAprovacao/OcorrenciaTemplate';
import RetencaoTemplate from '~/Components/Negocios/CaixaAprovacao/RetencaoTemplate';
import RateioTemplate from '~/Components/Negocios/CaixaAprovacao/RateioTemplate';
import TextoTemplate from '~/Components/Negocios/CaixaAprovacao/TextoTemplate';
import ResumoPagamentoTemplate from '~/Components/Negocios/CaixaAprovacao/ResumoPagamentoTemplate';

// Componente para zoom com controles externos na digitalização
const DigitalizacaoViewer = ({ digitalizacao }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - 0.5, 1);
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = e => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = e => {
    if (isDragging && zoomLevel > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
      e.preventDefault();
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch events for mobile
  const handleTouchStart = e => {
    if (zoomLevel > 1 && e.touches.length === 1) {
      e.preventDefault();
      setIsDragging(true);
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      });
    }
  };

  const handleTouchMove = e => {
    if (isDragging && zoomLevel > 1 && e.touches.length === 1) {
      e.preventDefault();
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
    }
  };

  const handleTouchEnd = e => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Reset position when zoom changes
  React.useEffect(() => {
    if (zoomLevel === 1) {
      setPosition({ x: 0, y: 0 });
    }
  }, [zoomLevel]);

  return (
    <div className="d-flex flex-column" style={{ height: '80vh' }}>
      {digitalizacao.Thumb && (
        <div className="mb-1 d-flex justify-content-center gap-1">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleZoomOut}
            disabled={zoomLevel <= 1}
            title="Diminuir zoom"
          >
            <FaSearchMinus />
          </button>
          <span className="align-self-center mx-2 small">
            {Math.round(zoomLevel * 100)}%
          </span>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleZoomIn}
            disabled={zoomLevel >= 3}
            title="Aumentar zoom"
          >
            <FaSearchPlus />
          </button>
          <button
            className="btn btn-outline-secondary btn-sm ms-1 ml-1"
            onClick={handleResetZoom}
            disabled={zoomLevel === 1}
            title="Resetar zoom"
          >
            <FaUndo />
          </button>
        </div>
      )}

      <div
        className="flex-grow-1 d-flex justify-content-center align-items-center"
        style={{
          overflow: zoomLevel > 1 ? 'hidden' : 'auto',
          position: 'relative',
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {digitalizacao.Thumb ? (
          <img
            src={digitalizacao.Thumb}
            alt="Prévia do documento"
            className="img-fluid"
            style={{
              maxHeight: zoomLevel === 1 ? '100%' : 'none',
              maxWidth: zoomLevel === 1 ? '100%' : 'none',
              transform: `scale(${zoomLevel}) translate(${
                position.x / zoomLevel
              }px, ${position.y / zoomLevel}px)`,
              transformOrigin: 'center',
              transition: isDragging ? 'none' : 'transform 0.3s ease',
              cursor:
                zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
              userSelect: 'none',
              pointerEvents: zoomLevel > 1 ? 'auto' : 'none',
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            draggable={false}
          />
        ) : (
          <div className="text-center">
            <p>Carregando prévia do documento...</p>
            <button
              className="btn btn-link"
              onClick={() =>
                window.open(
                  digitalizacao.url || digitalizacao.link_pdf?.url,
                  '_blank',
                  'noopener,noreferrer'
                )
              }
            >
              Clique aqui para abrir o PDF
            </button>
          </div>
        )}
      </div>

      <div className="mt-2 d-flex justify-content-center">
        <button
          className="btn btn-primary"
          onClick={() =>
            window.open(
              digitalizacao.url || digitalizacao.link_pdf?.url,
              '_blank',
              'noopener,noreferrer'
            )
          }
        >
          Abrir documento
        </button>
      </div>
    </div>
  );
};

export const orderingOptions = [
  {
    label: 'Vencimento (crescente)',
    value: 'vencimento-crescente',
    func: (c1, c2) => (c1.data_vencimento < c2.data_vencimento ? -1 : 1),
  },
  {
    label: 'Vencimento (decrescente)',
    value: 'vencimento-decrescente',
    func: (c1, c2) => (c1.data_vencimento < c2.data_vencimento ? 1 : -1),
  },
  {
    label: 'Pagar em (crescente)',
    value: 'pagar-em-crescente',
    func: (c1, c2) => {
      const a = c1.pagar_em || '';
      const b = c2.pagar_em || '';
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return a < b ? -1 : 1;
    },
  },
  {
    label: 'Pagar em (decrescente)',
    value: 'pagar-em-decrescente',
    func: (c1, c2) => {
      const a = c1.pagar_em || '';
      const b = c2.pagar_em || '';
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return a < b ? 1 : -1;
    },
  },
  {
    label: 'Valor (crescente)',
    value: 'valor-crescente',
    func: (c1, c2) => (c1.valor < c2.valor ? -1 : 1),
  },
  {
    label: 'Valor (decrescente)',
    value: 'valor-decrescente',
    func: (c1, c2) => (c1.valor < c2.valor ? 1 : -1),
  },
];

export const ordenacaoSolicitacao = [
  {
    label: 'Código da Solicitação (crescente)',
    value: 'codigo-crescente',
    func: (c1, c2) => (c1.codigo_solicitacao < c2.codigo_solicitacao ? -1 : 1),
  },
  {
    label: 'Código da Solicitação (decrescente)',
    value: 'codigo-decrescente',
    func: (c1, c2) => (c1.codigo_solicitacao < c2.codigo_solicitacao ? 1 : -1),
  },
  {
    label: 'Data Vencimento (crescente)',
    value: 'vencimento-crescente',
    func: (c1, c2) => (c1.data_vencimento < c2.data_vencimento ? -1 : 1),
  },
  {
    label: 'Data Vencimento (decrescente)',
    value: 'vencimento-decrescente',
    func: (c1, c2) => (c1.data_vencimento < c2.data_vencimento ? 1 : -1),
  },
  {
    label: 'Valor (crescente)',
    value: 'valor-crescente',
    func: (c1, c2) => (c1.valor < c2.valor ? -1 : 1),
  },
  {
    label: 'Valor (decrescente)',
    value: 'valor-decrescente',
    func: (c1, c2) => (c1.valor < c2.valor ? 1 : -1),
  },
];

export const blankView = {
  title: '',
  chave: {},
  content: null,
  render: () => {},
};

export const modalViews = {
  ocorrencias: {
    title: 'Ocorrências',
    chave: null,
    content: null,
    render: chave =>
      chave &&
      chave.ocorrencias.map((item, index) => (
        <OcorrenciaTemplate key={index} ocorrencia={item} />
      )),
  },
  retencoes: {
    title: 'Retenções',
    chave: null,
    content: null,
    render: chave =>
      chave &&
      chave.retencoes.map((item, index) => (
        <RetencaoTemplate key={index} retencao={item} />
      )),
  },
  rateios: {
    title: 'Rateios',
    chave: null,
    content: null,
    render: chave =>
      chave &&
      chave.rateios.map((item, index) => (
        <RateioTemplate key={index} rateio={item} />
      )),
  },
  digitalizacao: {
    title: 'Digitalização',
    chave: null,
    content: null,
    link: null,
    render: digitalizacao =>
      digitalizacao && <DigitalizacaoViewer digitalizacao={digitalizacao} />,
  },
  texto: {
    title: 'Texto',
    content: null,
    render: chave => <TextoTemplate chave={chave} />,
  },
  resumoPagamento: {
    title: 'Resumo do Pagamento - Rescisão',
    chave: null,
    content: null,
    render: chave => <ResumoPagamentoTemplate chave={chave} />,
  },
};

export const modalViewsSolicitacaoDespesaProjeto = {
  digitalizacao: {
    title: 'Digitalização',
    solicitacao: null,
    content: null,
    render: digitalizacao =>
      digitalizacao && <DigitalizacaoViewer digitalizacao={digitalizacao} />,
  },
};
